import React, { useState, useRef, useEffect } from "react";
import "./TitleAndTagCompetition.css";
import { Filter } from "bad-words";
import { useNavigate } from "react-router-dom";
import BottomNav from "./BottomNav";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { FaArrowLeft } from "react-icons/fa";

const sectors = [
  "Technology", "Health", "Education", "Business", "Lifestyle",
  "Media", "Environment", "Society", "Mobility", "Infrastructure", "Arts", "Sports"
];

const TitleAndTagCompetition = () => {
  const filter = new Filter();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [showTags, setShowTags] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const tagListRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [error, setError] = useState("");
  const [uid, setUid] = useState(null);

  const auth = getAuth();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => setUid(user ? user.uid : null));
    return unsub;
  }, []);

  const isFormValid =
    title.trim() !== "" &&
    selectedTags.length > 0 &&
    title.length <= 100 &&
    !filter.isProfane(title);

  const showPopupMessage = (message) => {
    setPopupMessage(message);
    setShowPopup(false);
    setTimeout(() => {
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 2500);
    }, 10);
  };

  const handleContinue = async () => {
    const charLimit = 250;
    if (!title.trim()) return showPopupMessage("Title cannot be empty.");
    if (title.length > charLimit) return showPopupMessage(`Title should not exceed ${charLimit} characters.`);
    if (selectedTags.length === 0) return showPopupMessage("Please select at least one tag.");
    if (!uid) return showPopupMessage("Please sign in to continue.");

    setIsLoading(true);
    navigate("/DescriptionCompetition", { state: { title, tags: selectedTags } });
  };

  const toggleTag = (tag, isDisabled) => {
    if (isDisabled) {
      showPopupMessage("You can only select up to 2 tags.");
      return;
    }
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.clientX >= document.documentElement.clientWidth) return;
      if (tagListRef.current && !tagListRef.current.contains(e.target)) {
        setShowTags(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div className="idea-form-page">
        <header className="idea-form-header">
          <button
            type="button"
            className="back-btn"
            aria-label="Go back"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft />
          </button>
          <h1 className="page-title">Title &amp; Tag</h1>
        </header>

        <main className="paper-card" ref={tagListRef}>
          <span className="paper-pin" aria-hidden="true"></span>

          <div className="field">
            <label className="field-label" htmlFor="idea-title">
              Your idea, in one line
            </label>
            <input
              id="idea-title"
              className="paper-input"
              type="text"
              placeholder="e.g. A rooftop garden network for apartments"
              value={title}
              maxLength={100}
              onChange={(e) => {
                const text = e.target.value;
                setTitle(text);
                setError(filter.isProfane(text) ? "Inappropriate language is not allowed." : "");
              }}
              required
            />
            {error && <p className="field-error">{error}</p>}
            <div className={`char-counter ${title.length === 100 ? "limit-exceeded" : ""}`}>
              {title.length} / 100
            </div>
          </div>

          <div className="field">
            <label className="field-label" id="tags-label">
              Pick up to 2 categories
            </label>

            <div
              className="tag-dropdown"
              role="button"
              tabIndex={0}
              aria-expanded={showTags}
              aria-labelledby="tags-label"
              onClick={() => setShowTags(!showTags)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setShowTags(!showTags)}
            >
              <span className={selectedTags.length === 0 ? "placeholder" : ""}>
                {selectedTags.length === 0 ? "Add tags" : selectedTags.join(", ")}
              </span>
              <span className="arrow">{showTags ? "▲" : "▼"}</span>
            </div>

            <div className={`tag-list-wrap ${showTags ? "open" : ""}`}>
              <div className="tag-list">
                {sectors.map((sector) => {
                  const isChecked = selectedTags.includes(sector);
                  const isDisabled = !isChecked && selectedTags.length >= 2;
                  return (
                    <label key={sector} className={`tag-chip ${isDisabled ? "is-disabled" : ""}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isDisabled}
                        onClick={() => toggleTag(sector, isDisabled)}
                        readOnly
                      />
                      <span className="tag-chip-stamp" aria-hidden="true"></span>
                      {sector}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </main>

        <div className="continue-wrapper">
          <button
            className="continue-btn"
            disabled={!isFormValid || isLoading}
            onClick={() => {
              if (filter.isProfane(title)) {
                showPopupMessage("Please remove inappropriate language from the title.");
              } else if (!isFormValid) {
                showPopupMessage("Please enter a title and select at least 1 tag.");
              } else {
                handleContinue();
              }
            }}
          >
            {isLoading ? <span className="loader"></span> : "Continue"}
          </button>
        </div>

        {showPopup && <div className="popup">{popupMessage}</div>}
        {showToast && <div className="toast">You can only select up to 2 tags.</div>}
      </div>

      <BottomNav />
    </>
  );
};

export default TitleAndTagCompetition;